import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { forgotPassword, verifyResetPasswordOtp, resetPassword } from '../api/client';
import { Diamond, ArrowRight, CheckCircle2, Eye, EyeOff, KeyRound } from 'lucide-react';
import { getTranslation as t, translateError as te, isRTL } from '../utils/translations';

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(null);
    const [resendTimer, setResendTimer] = useState(60);
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [loading, setLoading] = useState(false);

    // Resend countdown timer for Step 2
    React.useEffect(() => {
        if (step !== 2) return;
        setResendTimer(60);
        const interval = setInterval(() => {
            setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(interval);
    }, [step]);

    const handleRequestOtp = async (e) => {
        if (e) e.preventDefault();
        if (!email.trim()) {
            setError(te('Please enter a valid email address.'));
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMsg('');
        
        try {
            await forgotPassword({ email: email.trim() });
            setStep(2);
            setResendTimer(60);
            setSuccessMsg(te('If the email exists, a verification code has been sent.'));
        } catch (err) {
            console.error('OTP Request Error:', err);
            const resData = err.response?.data;
            if (resData && typeof resData === 'object' && resData.message) {
                setError(te(resData.message));
            } else {
                setError(te(err.message || 'Failed to send OTP. Please try again.'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendTimer > 0) return;
        await handleRequestOtp(null);
    };

    const handleVerifyOtpServer = async (e) => {
        if (e) e.preventDefault();
        if (otp.trim().length !== 6) {
            setError(te('Please enter the 6-digit verification code.'));
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMsg('');

        try {
            await verifyResetPasswordOtp({
                email: email.trim(),
                otp: otp.trim()
            });
            
            setStep(3);
        } catch (err) {
            console.error('Verify OTP Error:', err);
            const resData = err.response?.data;
            if (resData && typeof resData === 'object' && resData.message) {
                setError(te(resData.message));
            } else {
                setError(te(err.message || 'Verification failed. Please try again.'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!newPassword || newPassword.length < 8) {
            setError(te('Password must be at least 8 characters long.'));
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMsg('');

        try {
            await resetPassword({
                email: email.trim(),
                otp: otp.trim(),
                newPassword: newPassword
            });

            setSuccessMsg(te('Password reset successfully! Redirecting to login...'));
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err) {
            console.error('Reset Password Error:', err);
            const resData = err.response?.data;
            if (resData && typeof resData === 'object' && resData.message) {
                setError(te(resData.message));
            } else {
                setError(te(err.message || 'Failed to reset password. Please try again.'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (index, value) => {
        if (!/^[0-9]*$/.test(value)) return;
        let newOtpArray = otp.padEnd(6, ' ').split('');
        newOtpArray[index] = value || ' ';
        const newOtp = newOtpArray.join('').trimRight();
        setOtp(newOtp);

        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-input-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (!otp[index] || otp[index] === ' ') {
                if (index > 0) {
                    const prevInput = document.getElementById(`otp-input-${index - 1}`);
                    if (prevInput) {
                        prevInput.focus();
                        let newOtpArray = otp.padEnd(6, ' ').split('');
                        newOtpArray[index - 1] = ' ';
                        setOtp(newOtpArray.join('').trimRight());
                    }
                }
            } else {
                let newOtpArray = otp.padEnd(6, ' ').split('');
                newOtpArray[index] = ' ';
                setOtp(newOtpArray.join('').trimRight());
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            document.getElementById(`otp-input-${index - 1}`)?.focus();
        } else if (e.key === 'ArrowRight' && index < 5) {
            document.getElementById(`otp-input-${index + 1}`)?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').slice(0, 6).replace(/[^0-9]/g, '');
        if (pasted) {
            setOtp(pasted);
            const focusIndex = Math.min(pasted.length, 5);
            document.getElementById(`otp-input-${focusIndex}`)?.focus();
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-content">
                <div className="auth-split-card">
                    {/* Form Panel */}
                    <div className="auth-form-panel">
                        <div className="auth-panel-header">
                            <h2>{step === 1 ? t('RESET_PASSWORD') : step === 2 ? t('VERIFY_EMAIL') : t('NEW_PASSWORD')}</h2>
                            <p>{step === 1 ? t('FORGOT_PASSWORD_SUBTITLE') : step === 2 ? t('ENTER_6DIGIT_CODE') : t('ENTER_NEW_PASSWORD')}</p>
                        </div>

                        {error && <div className="auth-error">{error}</div>}
                        {successMsg && <div className="auth-success" style={{ background: '#f0fdf4', color: '#16a34a', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', border: '1px solid #bbf7d0', display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <CheckCircle2 size={18} /> {successMsg}
                        </div>}

                        {step === 1 ? (
                            <form onSubmit={handleRequestOtp} className="auth-form">
                                <div className="input-group">
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder=" "
                                        required
                                    />
                                    <label htmlFor="email">{t('EMAIL_ADDRESS')}</label>
                                </div>

                                <button type="submit" className="btn-primary auth-submit" disabled={loading} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                                    {loading ? t('SENDING_CODE') : <>{t('SEND_RESET_LINK')} <ArrowRight size={18} /></>}
                                </button>
                                
                                <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: 'var(--gray-300)' }}>
                                    {t('ALREADY_HAVE_ACCOUNT')} <span style={{ color: '#111827', cursor: 'pointer', fontWeight: '700' }} onClick={() => navigate('/login')}>{t('SIGN_IN')}</span>
                                </p>
                            </form>
                        ) : step === 2 ? (
                            <form onSubmit={handleVerifyOtpServer} className="auth-form otp-card-container">
                                <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                                    <span style={{ fontSize: '13.5px', color: '#4b5563', lineHeight: 1.5 }}>
                                        {t('CODE_SENT_TO')}
                                    </span>
                                    <div style={{ color: '#111827', fontWeight: '800', fontSize: '14.5px', marginTop: '4px', wordBreak: 'break-all' }}>
                                        {email}
                                    </div>
                                </div>

                                <div className="otp-nodes-wrapper">
                                    {[0, 1, 2, 3, 4, 5].map((index) => (
                                        <div 
                                            key={index}
                                            className={`otp-node ${otp[index] && otp[index] !== ' ' ? 'has-value' : ''} ${focusedIndex === index ? 'is-focused' : ''}`}
                                        >
                                            <svg className="otp-node-svg" viewBox="0 0 54 62">
                                                <rect
                                                    x="2"
                                                    y="2"
                                                    width="50"
                                                    height="58"
                                                    rx="14"
                                                    className="trace"
                                                />
                                            </svg>
                                            <input
                                                id={`otp-input-${index}`}
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                maxLength="1"
                                                value={otp[index] && otp[index] !== ' ' ? otp[index] : ''}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                onFocus={() => setFocusedIndex(index)}
                                                onBlur={() => setFocusedIndex(null)}
                                                onPaste={handleOtpPaste}
                                                className="otp-square"
                                                autoComplete="one-time-code"
                                            />
                                            {index < 5 && <div className="otp-laser-connector" />}
                                        </div>
                                    ))}
                                </div>

                                <div className="otp-resend-row">
                                    {resendTimer > 0 ? (
                                        <span>
                                            {`${t('DIDNT_RECEIVE_CODE')} ${resendTimer}s`}
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            className="otp-resend-btn"
                                            onClick={handleResendOtp}
                                            disabled={loading}
                                        >
                                            {t('RESEND_VERIFICATION_CODE')}
                                        </button>
                                    )}
                                </div>

                                <button 
                                    type="submit" 
                                    className="btn-primary auth-submit" 
                                    disabled={loading || otp.trim().length !== 6}
                                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', background: '#111827', color: '#ffffff', borderRadius: '12px', height: '48px', fontWeight: '700', cursor: otp.trim().length === 6 ? 'pointer' : 'not-allowed', opacity: otp.trim().length === 6 ? 1 : 0.6 }}
                                >
                                    {loading ? t('VERIFYING') : <>{t('VERIFY_CODE')} <ArrowRight size={18} /></>}
                                </button>
                                
                                <button 
                                    type="button" 
                                    className="btn-secondary" 
                                    style={{ width: '100%', marginTop: '10px', borderRadius: '12px', height: '42px', fontWeight: '600', border: '1px solid #e5e7eb', background: '#f9fafb' }} 
                                    onClick={() => setStep(1)} 
                                    disabled={loading}
                                >
                                    {t('CHANGE_EMAIL')}
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleResetPassword} className="auth-form">
                                <div className="input-group password-group">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="newPassword"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder=" "
                                        required
                                    />
                                    <label htmlFor="newPassword">{t('PASSWORD')}</label>
                                    <button
                                        type="button"
                                        className="toggle-password"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                
                                <p style={{ fontSize: '11px', color: 'var(--gray-400)', marginTop: '-12px', marginBottom: '15px' }}>
                                    {t('PASSWORD_REQUIREMENTS')}
                                </p>

                                <button type="submit" className="btn-primary auth-submit" disabled={loading}>
                                    {loading ? t('SAVING_PROFILE') : t('SEND_RESET_LINK')}
                                </button>
                                
                                <button type="button" className="btn-secondary" style={{ width: '100%', marginTop: '10px' }} onClick={() => setStep(2)} disabled={loading}>
                                    {t('BACK')}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Side Interactive Banner */}
                    <div className="auth-side-banner">
                        <div className="auth-side-badge-icon">
                            <KeyRound size={36} />
                        </div>
                        <div className="auth-side-tag">ACCOUNT RECOVERY</div>
                        <h2>{t('REMEMBER_PASSWORD')}</h2>
                        <p>{t('REMEMBER_PASSWORD_DESC')}</p>
                        <Link to="/login" className="auth-side-btn">
                            {t('BACK_TO_SIGN_IN')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
